"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Wine } from "lucide-react";

const RECIPE = [
  { name: "Tequila Blanco", amount: "1.5 oz", from: 47.3, to: 45.8 },
  { name: "Triple Sec", amount: "0.5 oz", from: 22.1, to: 21.6 },
  { name: "Fresh Lime", amount: "0.5 oz", from: 18.4, to: 17.9 },
];

export default function LiveBarTabsRecipeScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0A0814] text-white overflow-hidden">
      <div
        className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(251,146,60,0.15) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          {/* Copy */}
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#FB923C" }}>
                {t("Recipe-level pours", "Pours por receta")}
              </div>
              <h2 className="text-4xl md:text-[56px] font-black tracking-[-0.03em] leading-[1.02] mb-6">
                {t("Every pour. ", "Cada pour. ")}
                <br />
                <span className="text-white/30">{t("Every bottle.", "Cada botella.")}</span>
              </h2>
              <p className="text-[16px] font-medium text-white/55 leading-relaxed mb-6">
                {t(
                  "Margarita rings as a single tap. The bottle of tequila loses 1.5 oz. Triple Sec loses 0.5 oz. Lime loses 0.5 oz. Variance reports tell you who's heavy-pouring.",
                  "La Margarita se ringea como un solo tap. La botella de tequila pierde 1.5 oz. Triple Sec pierde 0.5 oz. Limón pierde 0.5 oz. El reporte de varianza te dice quién está pour-eando pesado."
                )}
              </p>

              <div className="inline-flex items-center gap-2.5 bg-white/[0.04] border border-white/10 px-4 py-3 rounded-full">
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(251,146,60,0.15)" }}>
                  <Wine className="w-3.5 h-3.5" style={{ color: "#FB923C" }} />
                </div>
                <div className="text-[13px] font-bold text-white">
                  {t("Pours → tab → inventory, in one tap", "Pours → tab → inventario, en un tap")}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Recipe card */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
              className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[24px] p-7"
            >
              {/* Header */}
              <div className="flex items-baseline justify-between mb-6">
                <div>
                  <div className="text-[10px] font-black tracking-widest uppercase text-white/45 mb-1">
                    {t("Recipe", "Receta")}
                  </div>
                  <div className="text-[22px] font-black text-white">Margarita</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-white/45">
                    {t("Sells for", "Se vende a")}
                  </div>
                  <div
                    className="text-[24px] font-black tabular-nums"
                    style={{
                      background: "linear-gradient(135deg, #FB923C 0%, #F472B6 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    $14
                  </div>
                </div>
              </div>

              {/* Recipe lines */}
              <div className="space-y-3">
                {RECIPE.map((r, i) => (
                  <motion.div
                    key={r.name}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.5, delay: 0.3 + 0.15 * i }}
                    className="bg-white/[0.03] rounded-[14px] p-4 border border-white/5"
                  >
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="text-[14px] font-black text-white">{r.name}</div>
                      <div className="text-[13px] font-bold tabular-nums" style={{ color: "#FB923C" }}>
                        −{r.amount}
                      </div>
                    </div>
                    {/* Inventory bar with from→to animation */}
                    <div className="flex items-center gap-2 text-[10px] font-bold text-white/50">
                      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: `${(r.from / 50) * 100}%` }}
                          whileInView={{ width: `${(r.to / 50) * 100}%` }}
                          viewport={{ once: true, margin: "-80px" }}
                          transition={{ duration: 1.0, delay: 0.6 + 0.15 * i, ease: [0.21, 0.47, 0.32, 0.98] }}
                          className="h-full rounded-full"
                          style={{
                            background: "linear-gradient(90deg, #7C3AED 0%, #F472B6 100%)",
                          }}
                        />
                      </div>
                      <span className="tabular-nums text-white/60 shrink-0 min-w-[80px] text-right">
                        {r.from.toFixed(1)} → <span style={{ color: "#FB923C" }}>{r.to.toFixed(1)}</span> oz
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Variance hint */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 1.4 }}
                className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between"
              >
                <div className="text-[11px] font-medium text-white/55">
                  {t("Variance report ready in the morning.", "Reporte de varianza listo en la mañana.")}
                </div>
                <div
                  className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full"
                  style={{
                    backgroundColor: "rgba(46,204,113,0.15)",
                    color: "#2ECC71",
                  }}
                >
                  {t("On target", "En target")}
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
